import React, { useEffect, useMemo, useState } from 'react';
import { getUserId } from '../../../utils/api';
import usePortfolio from './hooks/usePortfolio';
import { Spinner, ErrorAlert } from '../../components/shared';
import PositionRow from './components/PositionRow';

const ASSET_TYPE_LABELS = {
  FUND: 'Quỹ mở',
  STOCK: 'Cổ phiếu',
  ETF: 'ETF',
  CRYPTO_SPOT: 'Crypto',
  CRYPTO_FUTURES: 'Futures',
};

const ASSET_TYPE_ICONS = {
  FUND: 'M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z',
  STOCK: 'M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z',
};

function fmt(v) {
  if (v == null) return '—';
  return new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 0 }).format(v);
}

function AssetTypeFilter({ assetTypes, selected, onSelect }) {
  return (
    <div className="flex gap-2 overflow-x-auto">
      <button
        type="button"
        onClick={() => onSelect(null)}
        className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
          selected === null
            ? 'bg-blue-600 text-white'
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
        }`}
      >
        Tất cả ({assetTypes.reduce((s, t) => s + t.count, 0)})
      </button>
      {assetTypes.map((t) => (
        <button
          key={t.key}
          type="button"
          onClick={() => onSelect(t.key)}
          className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
            selected === t.key
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          {t.label} ({t.count})
        </button>
      ))}
    </div>
  );
}

function AssetGroupCard({ assetType, positions, expanded, onToggle }) {
  const totalCost = positions.reduce((s, p) => s + (p.costBasis || 0), 0);
  const totalValue = positions.reduce((s, p) => s + (p.currentAmount || 0), 0);
  const totalGain = totalValue - totalCost;
  const gainColor = totalGain > 0 ? 'text-green-600' : totalGain < 0 ? 'text-red-600' : 'text-gray-500';
  const iconPath = ASSET_TYPE_ICONS[assetType] || ASSET_TYPE_ICONS.STOCK;

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-4 py-4 text-left transition-colors hover:bg-gray-50"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50">
            <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d={iconPath} />
            </svg>
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">
              {ASSET_TYPE_LABELS[assetType] || assetType}
            </p>
            <p className="text-xs text-gray-500">{positions.length} vị thế</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-sm font-medium text-gray-900">{fmt(totalValue)}</p>
            <p className={`text-xs font-medium ${gainColor}`}>
              {totalGain > 0 ? '+' : ''}{fmt(totalGain)}
            </p>
          </div>
          <svg
            className={`h-5 w-5 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
          </svg>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-gray-100">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Mã</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Khối lượng</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Giá vốn TB</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Tổng giá vốn</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Giá hiện tại</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Giá trị</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Lãi/Lỗ</th>
                </tr>
              </thead>
              <tbody>
                {positions.map((pos) => (
                  <PositionRow key={pos.id} position={pos} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function PortfolioSummary({ positions }) {
  if (positions.length === 0) return null;

  const totalCost = positions.reduce((sum, p) => sum + (p.costBasis || 0), 0);
  const totalValue = positions.reduce((sum, p) => sum + (p.currentAmount || 0), 0);
  const totalGain = totalValue - totalCost;
  const gainPercent = totalCost > 0 ? (totalGain / totalCost) * 100 : 0;
  const gainColor = totalGain > 0 ? 'text-green-600' : totalGain < 0 ? 'text-red-600' : 'text-gray-500';

  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <p className="text-xs text-gray-500">Tổng giá vốn</p>
        <p className="mt-1 text-lg font-semibold text-gray-900">{fmt(totalCost)}</p>
      </div>
      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <p className="text-xs text-gray-500">Giá trị hiện tại</p>
        <p className="mt-1 text-lg font-semibold text-gray-900">{fmt(totalValue)}</p>
      </div>
      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <p className="text-xs text-gray-500">Lãi/Lỗ</p>
        <p className={`mt-1 text-lg font-semibold ${gainColor}`}>{fmt(totalGain)}</p>
      </div>
      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <p className="text-xs text-gray-500">% Lãi/Lỗ</p>
        <p className={`mt-1 text-lg font-semibold ${gainColor}`}>
          {totalGain > 0 ? '+' : ''}{gainPercent.toFixed(2)}%
        </p>
      </div>
    </div>
  );
}

function AllPositionsTable({ positions }) {
  if (positions.length === 0) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white py-12 text-center shadow-sm">
        <svg className="mx-auto h-12 w-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
        </svg>
        <p className="mt-3 text-sm text-gray-500">Chưa có vị thế nào</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Mã</th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Khối lượng</th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Giá vốn TB</th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Tổng giá vốn</th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Giá hiện tại</th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Giá trị</th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Lãi/Lỗ</th>
            </tr>
          </thead>
          <tbody>
            {positions.map((pos) => (
              <PositionRow key={pos.id} position={pos} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function PortfolioPage() {
  const {
    portfolios,
    selectedPortfolioId,
    positions,
    loading,
    syncing,
    error,
    initialize,
    selectPortfolio,
    syncPositions,
  } = usePortfolio();

  const [selectedAssetType, setSelectedAssetType] = useState(null);
  const [expandedTypes, setExpandedTypes] = useState({});

  const assetTypes = useMemo(() => {
    const typeMap = {};
    positions.forEach((p) => {
      const t = p.assetType || 'OTHER';
      if (!typeMap[t]) typeMap[t] = { key: t, label: ASSET_TYPE_LABELS[t] || t, count: 0 };
      typeMap[t].count++;
    });
    return Object.values(typeMap);
  }, [positions]);

  const groupedPositions = useMemo(() => {
    const groups = {};
    positions.forEach((p) => {
      const t = p.assetType || 'OTHER';
      if (!groups[t]) groups[t] = [];
      groups[t].push(p);
    });
    return groups;
  }, [positions]);

  const filteredPositions = useMemo(() => {
    if (!selectedAssetType) return positions;
    return positions.filter((p) => (p.assetType || 'OTHER') === selectedAssetType);
  }, [positions, selectedAssetType]);

  const toggleExpand = (type) => {
    setExpandedTypes((prev) => ({ ...prev, [type]: !prev[type] }));
  };

  useEffect(() => {
    initialize();
  }, []);

  useEffect(() => {
    if (assetTypes.length > 0 && Object.keys(expandedTypes).length === 0) {
      const initial = {};
      assetTypes.forEach((t) => { initial[t.key] = true; });
      setExpandedTypes(initial);
    }
  }, [assetTypes]);

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Quản lý tài sản</h1>
          <p className="mt-1 text-sm text-gray-500">Danh mục đầu tư của bạn</p>
        </div>
        <button
          type="button"
          disabled={syncing}
          onClick={() => syncPositions(getUserId())}
          className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:opacity-50"
        >
          <svg className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182M2.985 19.644l3.181-3.182" />
          </svg>
          {syncing ? 'Đang đồng bộ...' : 'Đồng bộ'}
        </button>
      </div>

      {portfolios.length > 1 && (
        <div className="flex gap-2 overflow-x-auto">
          {portfolios.map((p) => (
            <button
              key={p.id}
              onClick={() => selectPortfolio(p.id)}
              className={`whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                p.id === selectedPortfolioId
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {p.name} ({p.positionCount})
            </button>
          ))}
        </div>
      )}

      <ErrorAlert error={error} />

      {loading && positions.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : (
        <>
          <PortfolioSummary positions={filteredPositions} />

          {assetTypes.length > 1 && (
            <AssetTypeFilter
              assetTypes={assetTypes}
              selected={selectedAssetType}
              onSelect={setSelectedAssetType}
            />
          )}

          {selectedAssetType ? (
            <AllPositionsTable positions={filteredPositions} />
          ) : (
            <div className="space-y-4">
              {Object.entries(groupedPositions).map(([type, typePositions]) => (
                <AssetGroupCard
                  key={type}
                  assetType={type}
                  positions={typePositions}
                  expanded={!!expandedTypes[type]}
                  onToggle={() => toggleExpand(type)}
                />
              ))}
              {positions.length === 0 && (
                <div className="rounded-xl border border-gray-200 bg-white py-12 text-center shadow-sm">
                  <svg className="mx-auto h-12 w-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
                  </svg>
                  <p className="mt-3 text-sm text-gray-500">Chưa có vị thế nào</p>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
